import { notification, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import routes from '@root/routes';
import api from '@shared/api';
import MediaPhotosSection from '@components/shared/Admin/EditPhotos/MediaPhotosSection';
import VideoTable from '@components/shared/Admin/VideoTable';
import type { DefaultPhotoUploadType } from '@components/shared/Admin/MediaUploader';
import type { DefaultVideoUploadType } from '@components/shared/Admin/VideoUploader';
import { CustomIcon } from '@components/shared';
import ThreeDContent from '@components/shared/Admin/ThreeDContent';
import GenerateMediaDirectoryButton from '@root/components/shared/GenerateMediaDirectoryButton';

import {
  AdminListingPhoto,
  AdminListingVideo,
  AdminBrochure,
  ContentShootRequest,
  SpaceFloorPlanPhoto,
  SpaceAdditionalPhoto,
  AdminFloorPlan,
  Room,
  VirtualTourSpotlights,
} from '@root/types';
import s from './ListingMediaTable.module.less';
import FloorPlanTable from './FloorPlanTable';
import ContentShootDetails from './ContentShootDetails';
import ExistingContent from './ExistingContent';
import AreaSelector from './AreaSelector';

export type Props = {
  fetchListing: () => void;
  photos: AdminListingPhoto[];
  video: AdminListingVideo | null;
  rooms: Room[];
  tourEmbedUrl: string | null;
  googleDriveMediaDirectoryId: string | null;
  googleDriveMediaDirectoryIdTwo: string | null;
  listingId: string;
  contentShootRequest: ContentShootRequest | null;
  formatNotificationErrorMessages: (messages: Array<string>) => HTMLElement;
  spaceFloorPlanPhotos: SpaceFloorPlanPhoto[];
  additionalPhotos: SpaceAdditionalPhoto[];
  spaceBrochure: AdminBrochure | null;
  buildingBrochure: AdminBrochure | null;
  floorPlan: AdminFloorPlan | null;
  virtualTourSpotlight: VirtualTourSpotlights;
};

const ListingMediaTable = ({
  photos,
  video,
  rooms,
  googleDriveMediaDirectoryId,
  googleDriveMediaDirectoryIdTwo,
  fetchListing,
  listingId,
  tourEmbedUrl,
  formatNotificationErrorMessages,
  contentShootRequest,
  spaceFloorPlanPhotos,
  additionalPhotos,
  spaceBrochure,
  buildingBrochure,
  floorPlan,
  virtualTourSpotlight,
}: Props) => {
  const { t } = useTranslation('admin');

  const handleUploadPhotos = (uploadedFiles: DefaultPhotoUploadType[]) =>
    api.post(routes.api.admin.listingPhotosCreate(listingId), {
      photos: uploadedFiles,
    });

  const handleUpdatePhotoPosition = async (photoIds: string[]) => {
    const response = await api.put(routes.api.admin.listingPhotosOrder(listingId), {
      photo_ids: photoIds,
    });

    if (response.ok) {
      notification.success({ message: t('common.photos.orderSaveSuccess') });
      fetchListing();
    } else {
      notification.error({ message: t('common.photos.orderSaveFailure') });
    }
  };

  const handleDeletePhoto = async (photoId: string) => {
    const response = await api.delete(routes.api.admin.listingPhoto(listingId, photoId));
    const json = await response.json();

    if (response.ok) {
      fetchListing();
      notification.success({ message: json.message });
    } else {
      notification.error({ message: json.errors[0].message });
    }
  };

  const videoUploadRequest = (uploadedFile: DefaultVideoUploadType) =>
    api.post(routes.api.admin.listingVideoCreate(listingId), {
      cloudinary_id: uploadedFile.cloudinaryId,
      filename: uploadedFile.filename,
      processed_at: uploadedFile.processedAt,
    });

  const updateTourEmbedUrl = async (value: string) => {
    const resp = await api.put(routes.api.admin.listing(listingId), {
      tour_embed_url: value,
    });
    const json = await resp.json();

    if (resp.ok) {
      fetchListing();
      return true;
    }
    notification.error({ message: formatNotificationErrorMessages(json.messages) });
    return false;
  };

  const updateVirtualTourSpotlight = async (newValue: Exclude<VirtualTourSpotlights, 'unset'>) => {
    const resp = await api.put(routes.api.admin.listing(listingId), {
      virtualTourSpotlight: newValue,
    });

    if (resp.ok) {
      fetchListing();
      return true;
    }
    return false;
  };

  const deleteListingVideo = async () => {
    // eslint-disable-next-line no-alert
    if (window.confirm(t('media.deleteConfirmation'))) {
      const resp = await api.delete(routes.api.admin.listingVideo(listingId, video?.id));
      const json = await resp.json();
      if (resp.ok) {
        fetchListing();
        notification.success({ message: json.message });
      } else {
        notification.error({ message: json.errors[0].message });
      }
    }
  };

  const updateThumbnailPosterTimestamp = async (posterStartOffset: number | null) => {
    const resp = await api.put(routes.api.admin.listingVideo(listingId, video?.id), {
      posterStartOffset,
    });
    return resp;
  };

  return (
    <div className="u-p-top-3x">
      <div>
        <FloorPlanTable floorPlan={floorPlan} fetchListing={fetchListing} listingId={listingId} />
      </div>
      <div className={s.mediaTableSection}>
        <MediaPhotosSection
          title={t('media.listingPhotosTitle')}
          photos={photos}
          googleDriveMediaDirectoryId={googleDriveMediaDirectoryId}
          googleDriveMediaDirectoryIdTwo={googleDriveMediaDirectoryIdTwo}
          fetchRecord={fetchListing}
          onPhotoUpload={handleUploadPhotos}
          onUpdatePhotoPosition={handleUpdatePhotoPosition}
          onDeletePhoto={handleDeletePhoto}
          additionalColumn={{
            title: (
              <>
                <span>{t('media.mediaManagement.columns.area')}</span>
                <Tooltip
                  overlayClassName={s.areaOverlay}
                  trigger={['hover', 'click']}
                  placement="top"
                  title={t('media.mediaManagement.areaInfo')}
                >
                  <span data-testid="area-tooltip" className={s.areaTooltip}>
                    <CustomIcon type="info" />
                  </span>
                </Tooltip>
              </>
            ),
            render: (photo: AdminListingPhoto) => (
              <AreaSelector
                areas={rooms}
                listingId={listingId}
                photo={photo}
                fetchListing={fetchListing}
              />
            ),
          }}
        />
      </div>
      <div className={s.mediaTableSection}>
        <VideoTable
          key={video?.id}
          video={video}
          onSuccess={fetchListing}
          spotlightChecked={!!video && !!tourEmbedUrl}
          videoTableTitle={t('media.listingVideoTitle')}
          videoUploadRequest={videoUploadRequest}
          onVideoDelete={deleteListingVideo}
          onPosterUpdate={updateThumbnailPosterTimestamp}
          onUpdateVirtualTourSpotlight={updateVirtualTourSpotlight}
          virtualTourSpotlight={virtualTourSpotlight}
          totalVariableColumnsWidth="52vw"
        />
      </div>
      <div className={s.mediaTableSection}>
        <ThreeDContent
          tourEmbedUrl={tourEmbedUrl}
          onTourEmbedUrlUpdate={updateTourEmbedUrl}
          onVirtualTourSpotlightUpdate={updateVirtualTourSpotlight}
          virtualTourSpotlight={virtualTourSpotlight}
          hasVideo={!!video}
          totalVariableColumnsWidth="52vw"
          tableTitle={t('media.listing3dContentTitle')}
        />
      </div>
      {/* eslint-disable-next-line no-nested-ternary */}
      {googleDriveMediaDirectoryIdTwo ? null : (
        <div className={s.mediaTableSection}>
          <div className={s.generateMediaSection}>
            <GenerateMediaDirectoryButton
              entityId={listingId}
              afterCreate={() => fetchListing()}
              disabled={!!googleDriveMediaDirectoryIdTwo}
              route={routes.api.admin.listingMediaDirectory}
            />
          </div>
        </div>
      )}
      {contentShootRequest ? (
        <div className={s.mediaTableSection}>
          <ContentShootDetails contentShootRequest={contentShootRequest} />
        </div>
      ) : null}
      <div className={s.mediaTableSection}>
        <ExistingContent
          floorPlanPhotos={spaceFloorPlanPhotos}
          additionalPhotos={additionalPhotos}
          spaceBrochure={spaceBrochure}
          buildingBrochure={buildingBrochure}
        />
      </div>
    </div>
  );
};

export default ListingMediaTable;
